import { User, useAuthStore } from '@/lib/stores/authStore';
import { createComponentLogger } from '@/lib/utils/logger';
import { NETWORK_ERROR_MESSAGE } from '@/lib/api/client';
import { fetchPractitionerRole } from './practitioner';
import {
  MenuItem,
  UserRole,
  ProjectConfig,
  DepartmentKey,
  DepartmentConfig,
} from '@/lib/types/authorization';
export type { ProjectConfig, DepartmentConfig };

const logger = createComponentLogger('authApi');

// Backend API base URL - configure in .env file
const AUTH_API_BASE_URL = import.meta.env.VITE_AUTH_API_URL;

/**
 * Project reference from API response
 */
export interface ProjectInfo {
  reference: string;
  display: string;
}

/**
 * Login request payload
 */
export interface LoginRequest {
  email: string;
  password: string;
  projectReference?: string;
}

/**
 * Backend login response structure
 * Supports v2, v3, and v4 API versions
 * Handles both single project (auto-login) and multiple projects (requires selection)
 */
export interface BackendLoginResponse {
  success: boolean;
  // Present when login is successful (single project or project selected)
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  tokenType?: string;
  profile?: {
    reference: string;
    display: string;
  };
  project?: {
    reference: string;
    display: string;
  };
  // Role from the backend
  role?: UserRole | string;
  // Display label for the role
  roleDisplay?: string;
  // What the role may do, from its AccessPolicy
  capabilities?: string[];
  // Menu items configured for the user
  menu?: MenuItem[];
  // Present when user has multiple projects and needs to select one
  requiresProjectSelection?: boolean;
  projects?: ProjectInfo[];
  message?: string;
  // Error field - user-friendly message from backend
  error?: string;
  // Per-project config (features, labels, Medplum URL)
  projectConfig?: ProjectConfig;
  // Department key identifying the doctor's department (e.g., "cardiology", "pediatrics")
  departmentKey?: DepartmentKey;
  // Department-specific case-sheet UI configuration
  departmentConfig?: DepartmentConfig | null;
}

/**
 * Login response for frontend - requires project selection
 */
export interface LoginRequiresProjectResponse {
  requiresProjectSelection: true;
  projects: ProjectInfo[];
  message: string;
}

/**
 * Login response for frontend - successful login
 */
export interface LoginSuccessResponse {
  requiresProjectSelection?: false;
  user: User;
  token: string;
  expires_in?: number;
  message?: string;
  projectConfig?: ProjectConfig;
  departmentKey?: DepartmentKey;
  departmentConfig?: DepartmentConfig | null;
}

const normalizeDepartmentKey = (departmentKey: unknown): DepartmentKey => {
  return typeof departmentKey === 'string' && departmentKey.trim() !== ''
    ? departmentKey.trim()
    : null;
};

/**
 * Combined login response type
 */
export type LoginResponse = LoginSuccessResponse | LoginRequiresProjectResponse;

/**
 * Logout response from backend
 */
export interface LogoutResponse {
  message: string;
}

/**
 * Current user response from backend
 */
export interface CurrentUserResponse {
  user: User;
  departmentKey?: string | null;
  departmentConfig?: DepartmentConfig | null;
}

export interface RefreshAccessTokenOptions {
  /**
   * Clear auth when refresh fails.
   * Keep this false for proactive/background refresh attempts where we want to avoid
   * logging the user out on a transient or tenant-specific refresh failure.
   */
  clearAuthOnFailure?: boolean;
}

/**
 * Maps HTTP status codes to user-friendly login error messages.
 * Used as a fallback when the backend's error message is not available
 * (e.g., response is not valid JSON, or JSON lacks an error field).
 */
function getLoginErrorMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input and try again.';
    case 401:
      return 'Invalid email or password.';
    case 403:
      return 'Access denied. Your account may be disabled or lacks access.';
    case 404:
      return 'Login service not found. Please contact administrator.';
    case 408:
      return 'The request timed out. Please try again.';
    case 429:
      return 'Too many login attempts. Please try again later.';
    case 500:
      return 'Server error. Please try again later.';
    case 502:
      return 'Server is temporarily unavailable. Please try again later.';
    case 503:
      return 'Service is temporarily unavailable. Please try again later.';
    case 504:
      return 'Server took too long to respond. Please try again later.';
    default:
      return 'Something went wrong. Please try again later.';
  }
}

/**
 * Authentication API Service
 *
 * Provides methods for user authentication using direct backend API:
 * - Login with username/password via backend auth endpoint
 * - Logout
 * - Get current user information
 *
 * All methods return Promise and throw Error on failure.
 */
export const authApi = {
  /**
   * Login with username and password
   *
   * Handles two scenarios:
   * 1. Single project: Returns tokens immediately
   * 2. Multiple projects: Returns project list for selection
   *
   * @param credentials - Username, password, and optional projectReference
   * @returns Promise with either user/token or project selection requirement
   * @throws {Error} - On invalid credentials or network error
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      logger.log('Login attempt', {
        baseUrl: AUTH_API_BASE_URL,
        hasProjectReference: !!credentials.projectReference,
      });

      // credentials: 'include' allows browser to receive and store HTTP-Only cookies
      const response = await fetch(`${AUTH_API_BASE_URL}/v2/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password,
          ...(credentials.projectReference && { projectReference: credentials.projectReference }),
        }),
      });

      let data: BackendLoginResponse;
      try {
        data = await response.json();
      } catch {
        // Response body is not valid JSON (e.g., HTML error page from proxy/nginx)
        logger.error('Login response is not valid JSON, status:', response.status);
        throw new Error(getLoginErrorMessage(response.status));
      }

      // Check if user needs to select a project (can come with non-200 status)
      if (data.requiresProjectSelection && data.projects) {
        return {
          requiresProjectSelection: true,
          projects: data.projects,
          message: data.message || 'Please select a project to continue',
        };
      }

      // Handle error responses - backend returns user-friendly messages
      if (!response.ok) {
        logger.error('Login error:', data.error);
        throw new Error(data.error || getLoginErrorMessage(response.status));
      }

      // Check for successful login with token
      if (!data.success || !data.accessToken) {
        logger.error('Login failed - invalid response:', {
          success: data.success,
          hasToken: !!data.accessToken,
        });
        throw new Error(data.error || 'Login failed. Please try again.');
      }

      // Extract user info from the profile reference
      const profileId = data.profile?.reference?.split('/')[1] || '';
      const profileDisplay = data.profile?.display || credentials.email;

      // Get project name from backend response
      const projectName = data.project?.display;

      // Extract the STABLE project ID from the reference ("Project/<uuid>").
      // projectName (display) is mutable — hospital-specific UI must gate on this ID.
      const projectReferenceStr = data.project?.reference || credentials.projectReference;
      const projectId = projectReferenceStr
        ? projectReferenceStr.replace('Project/', '')
        : undefined;

      // Extract role from response (directly from backend in v4, no extraction needed)
      const userRole: UserRole | string = data.role || 'practitioner';
      const roleDisplay: string | undefined = data.roleDisplay;

      // Extract menu from response (v4 returns transformed menu directly)
      const menu: MenuItem[] | undefined = data.menu;

      // Create initial user object with authorization data
      const initialUser: User = {
        id: profileId,
        username: profileDisplay,
        email: credentials.email,
        firstName: profileDisplay.includes('@')
          ? profileDisplay.split('@')[0]
          : profileDisplay.split(' ')[0] || '',
        lastName: profileDisplay.includes('@')
          ? ''
          : profileDisplay.split(' ').slice(1).join(' ') || '',
        role: userRole,
        roleDisplay,
        capabilities: data.capabilities,
        projectName,
        projectId,
        menu,
      };

      // Store auth data first so apiClient can use the token
      useAuthStore
        .getState()
        .setAuth(
          initialUser,
          data.accessToken,
          data.projectConfig,
          normalizeDepartmentKey(data.departmentKey),
          data.departmentConfig
        );

      // Store projectReference immediately so refresh calls during first dashboard mount
      // have access to it even before other async login steps finish.
      const finalProjectReference = data.project?.reference || credentials.projectReference;
      if (finalProjectReference) {
        localStorage.setItem('project-reference', finalProjectReference);
        logger.log('Stored project reference for refresh', {
          baseUrl: AUTH_API_BASE_URL,
          projectReference: finalProjectReference,
        });
      } else {
        logger.warn('Login succeeded without a project reference');
      }

      // Now fetch practitioner role data with authentication
      let practitionerRoleData = null;
      try {
        practitionerRoleData = await fetchPractitionerRole(profileId);
      } catch (error) {
        logger.warn('Failed to fetch PractitionerRole:', error);
      }

      // Update user with practitioner role data
      const finalUser: User = {
        ...initialUser,
        practitionerRole: practitionerRoleData,
      };

      // Update the stored user with practitioner role data
      useAuthStore.getState().updateUser(finalUser);

      const normalizedResponse: LoginSuccessResponse = {
        requiresProjectSelection: false,
        user: finalUser,
        token: data.accessToken,
        expires_in: data.expiresIn || 3600, // 1 hour (3600 seconds)
        projectConfig: data.projectConfig,
        departmentKey: normalizeDepartmentKey(data.departmentKey),
        departmentConfig: data.departmentConfig,
      };

      return normalizedResponse;
    } catch (error) {
      // Handle network errors from fetch() itself (no internet, DNS failure, CORS)
      if (error instanceof TypeError) {
        logger.error('Login network error:', error.message);
        throw new Error(NETWORK_ERROR_MESSAGE);
      }

      // Re-throw Error instances as-is (preserves descriptive messages from inner try)
      if (error instanceof Error) {
        logger.error('Login error:', error.message);
        throw error;
      }

      // Truly unknown errors
      logger.error('Login unexpected error:', error);
      throw new Error('An unexpected error occurred during login. Please try again.');
    }
  },

  /**
   * Logout the current user
   *
   * @returns Promise with logout message
   */
  logout: async (): Promise<LogoutResponse> => {
    try {
      // For direct auth, we just clear local state
      // No backend call needed since we're using JWT tokens
      logger.log('Logout successful (local)');
      return { message: 'Logged out successfully' };
    } catch (error) {
      logger.error('Logout error:', error);
      return { message: 'Logged out locally' };
    }
  },

  /**
   * Get current authenticated user information from /me endpoint
   *
   * @returns Promise with current user
   * @throws {Error} - On unauthorized or network error
   */
  getCurrentUser: async (): Promise<CurrentUserResponse> => {
    try {
      const response = await fetch(`${AUTH_API_BASE_URL}/v2/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth-token') || ''}`,
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch current user');
      }

      const userInfo = await response.json();

      // Map the response to our User interface
      // Role should come directly from the backend
      return {
        user: {
          id: userInfo.id || userInfo.sub || '',
          username: userInfo.username || userInfo.display || '',
          email: userInfo.email || '',
          firstName: userInfo.firstName || userInfo.given_name || '',
          lastName: userInfo.lastName || userInfo.family_name || '',
          role: userInfo.role, // Role from backend, not hardcoded
          roleDisplay: userInfo.roleDisplay,
          capabilities: userInfo.capabilities,
          specialization: userInfo.specialization,
          menu: userInfo.menu,
        },
        departmentKey: normalizeDepartmentKey(userInfo.departmentKey),
        departmentConfig: userInfo.departmentConfig,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to get current user';
      logger.error('getCurrentUser error:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Verify if current token is still valid
   * Note: This is a simple check - for production, decode JWT and check expiry
   *
   * @param token - The access token to verify
   * @returns Promise<boolean> - true if token appears valid
   */
  verifyToken: async (token?: string): Promise<boolean> => {
    try {
      if (!token) {
        return false;
      }

      // Basic JWT structure check
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }

      // Decode and check expiry
      try {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(base64));
        const exp = payload.exp;
        if (exp) {
          const now = Math.floor(Date.now() / 1000);
          return exp > now;
        }
        return true; // No expiry claim, assume valid
      } catch {
        return false;
      }
    } catch (error) {
      logger.error('Token verification error:', error);
      return false;
    }
  },
};

/**
 * Fetch project config for an existing session (token present but projectConfig missing).
 * Calls GET /auth/project-config with the stored bearer token.
 * Returns the ProjectConfig or null on any error.
 */
export async function fetchProjectConfig(): Promise<ProjectConfig | null> {
  try {
    const token = localStorage.getItem('auth-token');
    if (!token) return null;
    const response = await fetch(`${AUTH_API_BASE_URL}/auth/project-config`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    });
    if (!response.ok) return null;
    const data = await response.json();
    const config = data.projectConfig ?? data;
    return config && typeof config === 'object' && 'projectBaseUrl' in config
      ? (config as ProjectConfig)
      : null;
  } catch {
    return null;
  }
}

/**
 * Response from GET /v2/auth/me endpoint.
 * Contains the user profile, role, and menu needed to restore auth state after page refresh.
 */
export interface UserConfigurationResponse {
  id: string;
  username: string;
  email: string;
  role: UserRole | string;
  roleDisplay?: string;
  capabilities?: string[];
  menu: MenuItem[];
  profile: {
    reference: string;
    display: string;
  };
  project: {
    reference: string;
    display: string;
  };
}

/**
 * Result of fetching user configuration.
 * - success: data was fetched successfully
 * - unauthorized: token is invalid/expired (401)
 * - error: network or other error
 */
export type FetchUserConfigResult =
  | { status: 'success'; data: UserConfigurationResponse }
  | { status: 'unauthorized' }
  | { status: 'error' };

/**
 * Fetch user configuration for an existing session after page refresh.
 * Calls GET /v2/auth/me with the stored bearer token.
 * Returns a result object indicating success, unauthorized (401), or error.
 *
 * This ensures the frontend has the latest role and menu
 * from the backend, even if they were updated after the initial login.
 */
export async function fetchUserConfiguration(): Promise<FetchUserConfigResult> {
  try {
    const token = localStorage.getItem('auth-token');
    if (!token) return { status: 'unauthorized' };

    const response = await fetch(`${AUTH_API_BASE_URL}/v2/auth/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache',
      },
      credentials: 'include',
    });

    if (response.status === 401) {
      logger.warn('User is not authenticated (401)');
      return { status: 'unauthorized' };
    }

    if (!response.ok) {
      logger.warn('Failed to fetch user configuration:', response.status);
      return { status: 'error' };
    }

    const data: UserConfigurationResponse = await response.json();
    return { status: 'success', data };
  } catch (error) {
    logger.error('Error fetching user configuration:', error);
    return { status: 'error' };
  }
}

/**
 * FHIR Organization resource structure
 */
export interface FhirOrganization {
  id: string;
  name: string;
  active?: boolean;
  type?: Array<{
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
    text?: string;
  }>;
  telecom?: Array<{
    system?: string;
    value?: string;
  }>;
  address?: Array<{
    line?: string[];
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  }>;
}

/**
 * FHIR Bundle response for Organization search
 */
export interface FhirOrganizationBundle {
  resourceType: string;
  type: string;
  entry?: Array<{
    resource: FhirOrganization;
  }>;
}

/**
 * Fetch organizations for the current user.
 * Calls GET /api/hmis/fhir/R4/Organization with the stored bearer token.
 * Returns an array of Organization objects or empty array on error.
 */
export async function fetchOrganizations(): Promise<
  Array<{
    id: string;
    name: string;
    active?: boolean;
    type?: string;
    telecom?: Array<{ system?: string; value?: string }>;
    address?: Array<{
      line?: string[];
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
    }>;
  }>
> {
  try {
    const token = localStorage.getItem('auth-token');
    if (!token) return [];

    const response = await fetch(`${AUTH_API_BASE_URL}/api/hmis/fhir/R4/Organization`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/fhir+json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      logger.warn('Failed to fetch organizations:', response.status);
      return [];
    }

    const data: FhirOrganizationBundle = await response.json();

    // Extract organizations from FHIR Bundle
    const organizations = (data.entry || []).map((entry) => ({
      id: entry.resource.id,
      name: entry.resource.name,
      active: entry.resource.active,
      type: entry.resource.type?.[0]?.text || entry.resource.type?.[0]?.coding?.[0]?.display,
      telecom: entry.resource.telecom,
      address: entry.resource.address,
    }));

    return organizations;
  } catch (error) {
    logger.error('Error fetching organizations:', error);
    return [];
  }
}

/**
 * Token Refresh Utility
 *
 * Handles automatic token refresh when the access token expires.
 * Uses the refresh_token to get a new access_token from the backend.
 */

/**
 * Response from the /auth/refresh endpoint
 * Refresh token is NOT in the response body - it's set as HTTP-Only cookie
 */
interface RefreshTokenResponse {
  // snake_case (standard OAuth)
  access_token?: string;
  expires_in?: number;
  // camelCase (our backend)
  accessToken?: string;
  expiresIn?: number;
}

/**
 * Flag to prevent multiple simultaneous refresh attempts
 */
let isRefreshing = false;

/**
 * Queue of pending requests waiting for token refresh
 */
let refreshSubscribers: Array<(token: string) => void> = [];

/**
 * Notify all subscribers that token has been refreshed
 */
function onTokenRefreshed(newToken: string): void {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

/**
 * Add a subscriber to wait for token refresh
 */
function subscribeToTokenRefresh(callback: (token: string) => void): void {
  refreshSubscribers.push(callback);
}

/**
 * Delay helper for retry logic
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Attempt to refresh the access token using the HTTP-Only cookie
 *
 * The refresh token is stored in an HTTP-Only cookie and sent automatically by the browser.
 * We only need to send the projectReference in the request body.
 *
 * CRITICAL: This function now includes retry logic with exponential backoff to handle
 * transient network failures. It will retry up to 3 times on network errors or 5xx responses.
 * It will NOT retry on 401/403 (genuine auth failures).
 *
 * @returns Promise<string | null> - New access token or null if refresh failed
 */
export async function refreshAccessToken(
  options: RefreshAccessTokenOptions = {}
): Promise<string | null> {
  const { clearAuthOnFailure = true } = options;
  const { updateToken, clearAuth } = useAuthStore.getState();

  // Get projectReference from localStorage (stored during login)
  const projectReference = localStorage.getItem('project-reference');

  // Log token expiry info for debugging
  const currentToken = useAuthStore.getState().token;
  let tokenExpiryInfo = 'unknown';
  if (currentToken) {
    try {
      const parts = currentToken.split('.');
      if (parts.length === 3) {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(base64));
        if (payload.exp) {
          const expiryMs = payload.exp * 1000;
          const timeUntilExpiry = expiryMs - Date.now();
          tokenExpiryInfo = `${Math.round(timeUntilExpiry / 1000)}s remaining`;
        }
      }
    } catch {
      // Ignore decode errors
    }
  }

  logger.log('Refreshing access token', {
    baseUrl: AUTH_API_BASE_URL,
    hasProjectReference: !!projectReference,
    clearAuthOnFailure,
    tokenExpiry: tokenExpiryInfo,
  });

  if (!projectReference) {
    logger.error(
      'CRITICAL: No project reference found for token refresh - cannot refresh without project context. This indicates localStorage was cleared or corrupted.'
    );
    if (clearAuthOnFailure) {
      clearAuth();
    }
    return null;
  }

  // Validate that we have a token to refresh (even if expired)
  if (!currentToken) {
    logger.error(
      'CRITICAL: No access token found in store - cannot refresh. User may need to login again.'
    );
    if (clearAuthOnFailure) {
      clearAuth();
    }
    return null;
  }

  // If already refreshing, wait for the result
  if (isRefreshing) {
    logger.log('Token refresh already in progress, waiting for result');
    return new Promise<string | null>((resolve) => {
      subscribeToTokenRefresh((token) => resolve(token));
    });
  }

  isRefreshing = true;

  // Retry configuration: 3 attempts with exponential backoff (1s, 2s, 4s)
  const maxAttempts = 3;
  const baseDelayMs = 1000;

  try {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        logger.log(`Token refresh attempt ${attempt}/${maxAttempts}`);

        // credentials: 'include' sends the HTTP-Only refresh_token cookie automatically
        const response = await fetch(`${AUTH_API_BASE_URL}/v2/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            projectReference: projectReference,
          }),
        });

        // Don't retry on 401/403 - these are genuine auth failures
        if (response.status === 401 || response.status === 403) {
          logger.error(
            `Token refresh failed with ${response.status} - refresh token is invalid or expired`,
            {
              attempt,
              status: response.status,
            }
          );
          if (clearAuthOnFailure) {
            clearAuth();
          }
          return null;
        }

        // Retry on 5xx server errors
        if (response.status >= 500 && attempt < maxAttempts) {
          const delayMs = baseDelayMs * Math.pow(2, attempt - 1);
          logger.warn(`Token refresh failed with ${response.status}, retrying in ${delayMs}ms`, {
            attempt,
            maxAttempts,
            nextDelay: delayMs,
          });
          await delay(delayMs);
          continue;
        }

        if (!response.ok) {
          logger.error('Token refresh failed with non-retryable status:', {
            attempt,
            status: response.status,
            statusText: response.statusText,
          });
          if (clearAuthOnFailure) {
            clearAuth();
          }
          return null;
        }

        const data: RefreshTokenResponse = await response.json();

        // Handle both camelCase and snake_case responses
        const newAccessToken = data.accessToken || data.access_token;

        if (!newAccessToken) {
          logger.error('Token refresh response missing access token', { attempt });
          if (clearAuthOnFailure) {
            clearAuth();
          }
          return null;
        }

        // Update the access token in the store
        // Note: refresh token is handled via HTTP-Only cookie by the browser
        updateToken(newAccessToken);

        // Notify waiting requests
        onTokenRefreshed(newAccessToken);

        logger.log(`Token refresh successful on attempt ${attempt}`, {
          attempt,
          newTokenExpiry: data.expiresIn ? `${data.expiresIn}s` : 'unknown',
        });

        return newAccessToken;
      } catch (error) {
        // Network error or timeout - retry if we have attempts left
        const isNetworkError = error instanceof TypeError;

        if (isNetworkError && attempt < maxAttempts) {
          const delayMs = baseDelayMs * Math.pow(2, attempt - 1);
          logger.warn(`Token refresh network error, retrying in ${delayMs}ms`, {
            attempt,
            maxAttempts,
            error: error.message,
            nextDelay: delayMs,
          });
          await delay(delayMs);
          continue;
        }

        // Final attempt failed or non-network error
        logger.error('Token refresh error:', {
          attempt,
          error,
          isNetworkError,
        });

        if (attempt === maxAttempts) {
          if (clearAuthOnFailure) {
            clearAuth();
          }
          return null;
        }
      }
    }

    // Should not reach here, but just in case
    logger.error('Token refresh exhausted all retry attempts');
    if (clearAuthOnFailure) {
      clearAuth();
    }
    return null;
  } finally {
    isRefreshing = false;
  }
}
